import { TITLE_LIST, TITLES, type TitleDef, type TitleStyle } from '@nine/shared';
import { drop, taiji } from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { SCHOOL_COLORS } from '../school/kind';
import { defineIconKind } from '../store';

/** Titles (ฉายา): a hanging plaque in the title's style colour, marked with what earned it. */

const STYLE_COLORS: Readonly<Record<TitleStyle, Hex>> = {
  gold: '#f0c040',
  frost: '#9ad8ff',
  flame: '#ff7a3a',
  jade: '#4fc49a',
  blood: '#d42a32',
  violet: '#b07aff',
  rainbow: '#ffffff',
  ink: '#3a3440',
};

/** What a title was earned by, as a small mark on its plaque. */
function titleMark(p: Pix, t: TitleDef, x: number, y: number): void {
  const u = t.unlock;
  switch (u.kind) {
    case 'school':
    case 'badge':
      p.ell(x, y, 3, 3, SCHOOL_COLORS[u.school] ?? C.gold);
      if (u.kind === 'badge') for (let k = 0; k < u.tier; k++) p.px(x - 3 + k * 2, y + 4, C.white);
      return;
    case 'kills':
      for (let k = 0; k < 3; k++) p.line(x - 3 + k * 3, y - 3, x - 4 + k * 3, y + 3, C.white);
      return;
    case 'quest':
    case 'arena':
      p.line(x - 3, y + 3, x + 3, y - 3, C.steel).line(x - 3, y - 3, x + 3, y + 3, C.steel);
      return;
    case 'mastery':
      taiji(p, x, y, 3);
      return;
    case 'legend':
      for (let k = 0; k < 5; k++) p.line(x, y + 3, x - 4 + k * 2, y - 3, k % 2 ? C.steel : C.gold);
      return;
    case 'unique':
      p.ell(x, y, 3, 3, C.paper);
      p.line(x - 2, y + 2, x + 2, y - 2, C.ink);
      return;
    case 'ladder':
      p.line(x - 2, y - 4, x - 2, y + 4, C.paper).line(x + 2, y - 4, x + 2, y + 4, C.paper);
      for (let k = -3; k <= 3; k += 2) p.line(x - 2, y + k, x + 2, y + k, C.paper);
      return;
    case 'pass':
      p.rect(x - 3, y - 3, 6, 6, C.paper);
      p.rect(x - 2, y - 1, 4, 1, C.ink);
      return;
    default:
      drop(p, x, y + 1, 2.4, C.blood);
  }
}

defineIconKind('title', {
  label: 'ฉายา',
  ids: () => TITLE_LIST.map((t) => t.id),
  name: (id) => TITLES[id]?.name ?? id,
  tint: (id) => STYLE_COLORS[TITLES[id]?.style ?? 'gold'],
  plate: 'none',
  fallback: (id) => ({
    draw: (p) => {
      const t = TITLES[id];
      const style = t?.style ?? 'gold';
      const col = STYLE_COLORS[style];
      // A hanging plaque with tassels.
      p.line(9, 2, 9, 7, C.seal).line(23, 2, 23, 7, C.seal);
      p.rect(3, 7, 26, 16, style === 'ink' ? C.paper : C.gold);
      p.rect(5, 9, 22, 12, style === 'rainbow' ? '#ff6a6a' : mix(col, '#24142c', style === 'ink' ? 0 : 0.2));
      if (style === 'rainbow')
        ['#ff6a6a', '#ffb04a', '#f0e04a', '#5ad87a', '#5aa8ff', '#b07aff'].forEach((c, i) =>
          p.rect(5, 9 + i * 2, 22, 2, c),
        );
      p.line(6, 23, 5, 29, C.seal).line(26, 23, 27, 29, C.seal);
      if (t) titleMark(p, t, 16, 15);
    },
  }),
});
